// Shared canonical class-identity + label resolver.
//
// Registrations reference classes by a mix of identifiers:
//   - natural slug            "beginners-basketball"
//   - prefixed slug           "db:beginners-basketball"
//   - database UUID           "bb111111-…-000000000001"
//   - prefixed UUID           "db:bb111111-…"
//   - legacy / retired slug   "mommy-me-baking"
//
// ALL of these must resolve to one canonical record. This module is the ONLY
// place that knows how to do that. Admin surfaces must never render a raw id.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { classes as staticClasses } from "@/data/classes";
import { LEGACY_CLASS_ALIASES } from "@/lib/classAliasTable";

export { LEGACY_CLASS_ALIASES };

const UNKNOWN = "Class needs identification";

/**
 * Legacy slug → current class id. When a class is renamed or merged, add
 * the old slug here so historical interest / payment / waiting rows keep
 * resolving to the new class without a manual data migration.
 */


/**
 * Human titles for slugs that no longer exist as active classes (retired
 * offerings that we still keep history for). Prevents an unresolved label
 * from ever appearing in admin surfaces.
 */
export const LEGACY_CLASS_TITLES: Record<string, string> = {
  "automotive-102": "Automotive 102",
  "mommy-me-cooking": "Mommy & Me Cooking",
  "teen-apprenticeship-program": "Teen Apprenticeship Program",
  "english-grammar": "English Grammar",
  "innovation-playground": "Innovation Playground",
  "intro-metalworking": "Intro to Metalworking",
  "minecraft-club": "Minecraft Club",
};

/** Strip the "db:" transport prefix, if present. */
export function stripDbPrefix(id: string): string {
  return id.startsWith("db:") ? id.slice(3).trim() : id;
}

/** Normalize a raw class id through the legacy alias map (prefix-aware). */
export function normalizeClassId(id: string | null | undefined): string {
  const bare = stripDbPrefix((id ?? "").trim());
  return LEGACY_CLASS_ALIASES[bare] ?? bare;
}

export interface ClassLabelIndexRow {
  id: string;
  slug?: string | null;
  title: string;
}

/**
 * Lookup index: every known key (uuid, db:uuid, slug, db:slug) → record.
 * `Record<string, string>` title maps are still accepted for back-compat.
 */
export interface ClassLabelIndex {
  byKey: Record<string, { uuid: string | null; slug: string | null; title: string }>;
}

function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/** Build the canonical index from `classes` rows (id, slug, title). */
export function buildClassLabelIndex(rows: ClassLabelIndexRow[]): ClassLabelIndex {
  const byKey: ClassLabelIndex["byKey"] = {};
  const put = (key: string, value: { uuid: string | null; slug: string | null; title: string }) => {
    if (!key) return;
    byKey[key] = value;
    byKey[`db:${key}`] = value;
  };
  // Static catalog first (slug-only classes).
  for (const c of staticClasses) {
    put(c.id, { uuid: null, slug: c.id, title: c.title });
  }
  // Database rows override / enrich with the canonical UUID.
  for (const r of rows) {
    if (!r?.id) continue;
    const value = { uuid: r.id, slug: r.slug ?? null, title: r.title };
    put(r.id, value);
    if (r.slug) put(r.slug, value);
  }
  return { byKey };
}

/** Accept either the new index or a legacy `Record<string,string>` title map. */
function coerceIndex(index: ClassLabelIndex | Record<string, string>): ClassLabelIndex {
  if (index && typeof index === "object" && "byKey" in (index as any)) {
    return index as ClassLabelIndex;
  }
  const byKey: ClassLabelIndex["byKey"] = {};
  for (const c of staticClasses) {
    byKey[c.id] = { uuid: null, slug: c.id, title: c.title };
    byKey[`db:${c.id}`] = byKey[c.id];
  }
  for (const [k, title] of Object.entries((index ?? {}) as Record<string, string>)) {
    const bare = stripDbPrefix(k);
    const value = { uuid: isUuid(bare) ? bare : null, slug: isUuid(bare) ? null : bare, title };
    byKey[bare] = value;
    byKey[`db:${bare}`] = value;
  }
  return { byKey };
}

export interface ResolvedClassIdentity {
  /** Canonical grouping key — uuid when known, else canonical slug, else raw id. */
  canonicalKey: string;
  /** Canonical database UUID, when the class exists in the DB. */
  uuid: string | null;
  /** Canonical natural slug, when known. */
  slug: string | null;
  title: string;
  isUnknown: boolean;
  rawId: string;
  /** Every identifier known to point at this class. */
  aliases: string[];
}

export type ResolvedClassLabel = ResolvedClassIdentity;

/** Canonical resolver. Accepts ANY stored class identifier. */
export function resolveClassIdentity(
  id: string | null | undefined,
  index: ClassLabelIndex | Record<string, string>,
): ResolvedClassIdentity {
  const raw = (id ?? "").trim();
  const idx = coerceIndex(index);
  if (!raw) {
    return { canonicalKey: "", uuid: null, slug: null, title: UNKNOWN, isUnknown: true, rawId: "", aliases: [] };
  }

  const bare = stripDbPrefix(raw);
  const aliasTarget = LEGACY_CLASS_ALIASES[bare] ?? bare;

  const hit =
    idx.byKey[raw] ??
    idx.byKey[bare] ??
    idx.byKey[aliasTarget] ??
    idx.byKey[`db:${aliasTarget}`];

  if (hit) {
    const canonicalKey = hit.uuid ?? hit.slug ?? aliasTarget;
    const aliases = Array.from(
      new Set(
        [hit.uuid, hit.slug, aliasTarget, bare, raw]
          .filter(Boolean)
          .flatMap((v) => [v as string, `db:${v}`]),
      ),
    );
    return {
      canonicalKey,
      uuid: hit.uuid,
      slug: hit.slug,
      title: hit.title,
      isUnknown: false,
      rawId: raw,
      aliases,
    };
  }

  // Retired class we still keep history for.
  const legacyTitle = LEGACY_CLASS_TITLES[bare] ?? LEGACY_CLASS_TITLES[aliasTarget];
  if (legacyTitle) {
    return {
      canonicalKey: aliasTarget,
      uuid: null,
      slug: aliasTarget,
      title: legacyTitle,
      isUnknown: false,
      rawId: raw,
      aliases: [aliasTarget, `db:${aliasTarget}`, raw],
    };
  }

  return {
    canonicalKey: bare || raw,
    uuid: isUuid(bare) ? bare : null,
    slug: isUuid(bare) ? null : bare,
    title: UNKNOWN,
    isUnknown: true,
    rawId: raw,
    aliases: [raw, bare, `db:${bare}`],
  };
}

/** Back-compat alias of {@link resolveClassIdentity}. */
export function resolveClassLabel(
  id: string | null | undefined,
  index: ClassLabelIndex | Record<string, string>,
): ResolvedClassIdentity {
  return resolveClassIdentity(id, index);
}

/**
 * React hook: returns a stable canonical resolver plus loading flag.
 * Loads id/slug/title for every db-backed class once and keeps it in memory.
 */
export function useClassLabelResolver() {
  const [rows, setRows] = useState<ClassLabelIndexRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await (supabase as any).from("classes").select("id, slug, title");
      if (!mounted) return;
      setRows((data ?? []) as ClassLabelIndexRow[]);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const index = useMemo(() => buildClassLabelIndex(rows), [rows]);

  return {
    loading,
    index,
    resolve: (id: string | null | undefined) => resolveClassIdentity(id, index),
    /** Canonical grouping key for any stored identifier. */
    canonicalKeyOf: (id: string | null | undefined) => resolveClassIdentity(id, index).canonicalKey,
    /** Convenience: just the title. */
    titleOf: (id: string | null | undefined) => resolveClassIdentity(id, index).title,
  };
}
